import { PRESET_COLLECTION_KEYS, presetCollectionContract } from "../presets/collection-contracts.js";
import { browserPresetWorkspace } from "../presets/browser-workspace.js";
import { registerSearchableDropdown } from "./searchable-dropdown.js";
import {
  normalizePresetBase,
  presetFixedItems,
  presetContractForWorkspace,
  presetItemKey,
  presetSavedReferenceState,
  presetSavedReferencesReady,
  presetSavedItems,
  presetWorkspaceStateMessage,
  readPresetWorkspaceState,
  translatePresetText,
  trimString,
} from "./preset-workspace-ui-model.js";

const TAG_NAME = "fishy-preset-quick-switch";
const SEARCHABLE_DROPDOWN_TAG_NAME = "fishy-searchable-dropdown";
const LANGUAGE_CHANGE_EVENT = "fishystuff:languagechange";
const SEARCHABLE_DROPDOWN_OPEN_EVENT = "fishystuff:searchable-dropdown-open";
const SEARCHABLE_DROPDOWN_CLOSE_EVENT = "fishystuff:searchable-dropdown-close";
const HTMLElementBase = globalThis.HTMLElement ?? class {};
let nextQuickSwitchInstanceId = 1;

function toastHelper() {
  return globalThis.window?.__fishystuffToast ?? null;
}

function normalizeEntry(value, index = 0) {
  if (typeof value === "string") {
    return {
      collectionKey: trimString(value),
      order: index,
      contract: null,
    };
  }
  const collectionKey = trimString(value?.collectionKey || value?.key);
  return collectionKey
    ? {
        collectionKey,
        order: Number.isFinite(Number(value?.order)) ? Number(value.order) : index,
        contract: value?.contract ?? null,
      }
    : null;
}

export function normalizePresetQuickSwitchEntry(value, index = 0) {
  return normalizeEntry(value, index);
}

export function presetQuickSwitchEntries(entries = PRESET_COLLECTION_KEYS) {
  return (Array.isArray(entries) ? entries : [])
    .map(normalizeEntry)
    .filter(Boolean)
    .sort((left, right) => left.order - right.order)
    .map((entry) => ({ ...entry }));
}

function resolveContract(entry, contractResolver = presetCollectionContract) {
  return entry.contract ?? contractResolver(entry.collectionKey);
}

export function filterPresetQuickSwitchOptions(options, query) {
  const normalizedQuery = trimString(query).toLowerCase();
  if (!normalizedQuery) {
    return Array.isArray(options) ? options.slice() : [];
  }
  return (Array.isArray(options) ? options : []).filter((option) => (
    trimString(option.searchText || option.label).toLowerCase().includes(normalizedQuery)
  ));
}

export function buildPresetQuickSwitchRow(
  state,
  contract,
  translate = translatePresetText,
) {
  const fixed = presetFixedItems(contract);
  const saved = state?.kind === "ready" ? presetSavedItems(state) : [];
  const savedReferencesReady = presetSavedReferencesReady(state);
  const savedReferences = presetSavedReferenceState(state);
  const options = [...fixed, ...saved].map((item) => ({
    collectionKey: contract.key,
    key: item.key,
    kind: item.kind,
    id: item.id,
    label: item.name,
    revision: item.revision,
    revisionId: item.revisionId,
    searchText: `${item.name} ${item.id}`,
  }));
  const basedOn = normalizePresetBase(state?.current?.basedOn);
  const selectedOptionKey = presetItemKey(basedOn.kind, basedOn.id);
  const baseOption = options.find((option) => option.key === selectedOptionKey) ?? null;
  const ready = state?.kind === "ready";
  const modified = ready && state.current?.modified === true;
  const selectedLabel = modified
    ? translate("presets.quick_switch.current_modified", "Current work · {$name}", {
        name: baseOption?.label || translate("presets.current.untitled", "Untitled"),
      })
    : baseOption?.label || (ready
      ? translate("presets.current.untitled", "Current work")
      : presetWorkspaceStateMessage(state));
  return {
    collectionKey: contract.key,
    label: trimString(contract.info?.label) || contract.key,
    ready,
    modified,
    revision: state?.revision,
    selectedLabel,
    selectedStatus: modified
      ? translate("common.status.modified", "Modified")
      : translate("presets.quick_switch.current", "Current"),
    selectedOptionKey: baseOption?.key || "",
    emptyText: translate("presets.quick_switch.empty", "No presets available."),
    searchPlaceholder: translate("presets.quick_switch.search", "Search presets…"),
    errorText: !ready
      ? presetWorkspaceStateMessage(state)
      : savedReferencesReady ? "" : presetWorkspaceStateMessage(savedReferences),
    options,
  };
}

export function buildPresetQuickSwitchRows(
  states,
  entries = presetQuickSwitchEntries(),
  translate = translatePresetText,
  contractResolver = presetCollectionContract,
) {
  const stateFor = typeof states === "function"
    ? states
    : (collectionKey) => states?.get?.(collectionKey) ?? states?.[collectionKey];
  return entries.map((entry) => {
    const contract = resolveContract(entry, contractResolver);
    return buildPresetQuickSwitchRow(
      stateFor(entry.collectionKey) ?? { kind: "uninitialized", collectionKey: entry.collectionKey },
      contract,
      translate,
    );
  });
}

export function presetQuickSwitchRenderKey(rows) {
  return JSON.stringify((Array.isArray(rows) ? rows : []).map((row) => ({
    collectionKey: row.collectionKey,
    label: row.label,
    ready: row.ready,
    emptyText: row.emptyText,
    searchPlaceholder: row.searchPlaceholder,
    modified: row.modified,
    selectedLabel: row.selectedLabel,
    selectedStatus: row.selectedStatus,
    selectedOptionKey: row.selectedOptionKey,
    errorText: row.errorText,
    options: row.options,
  })));
}

export async function applyPresetQuickSwitchOption(workspace, option, expectedRevision) {
  if (!workspace || !option) {
    return null;
  }
  const options = { expectedRevision };
  if (option.kind === "preset" && typeof workspace.usePreset === "function") {
    return workspace.usePreset(option.collectionKey, option.id, options);
  }
  if (option.kind === "fixed" && typeof workspace.useFixed === "function") {
    return workspace.useFixed(option.collectionKey, option.id, options);
  }
  return null;
}

function createElement(tagName, className = "", text = "") {
  const element = document.createElement(tagName);
  if (className) {
    element.className = className;
  }
  if (text) {
    element.textContent = text;
  }
  return element;
}

function createIcon(alias, className = "size-4") {
  const normalized = trimString(alias) || "fishy-layout-fill";
  const spriteId = normalized.startsWith("fishy-") ? normalized : `fishy-${normalized}`;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", `fishy-icon ${className}`);
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttribute("width", "100%");
  use.setAttribute("height", "100%");
  use.setAttribute("href", `#${spriteId}`);
  svg.append(use);
  return svg;
}

function focusWithoutScroll(element) {
  if (!element?.focus) {
    return false;
  }
  try {
    element.focus({ preventScroll: true });
  } catch (_error) {
    element.focus();
  }
  return true;
}

function selectorFromEvent(event) {
  const dropdown = event?.target;
  return dropdown instanceof Element && dropdown.matches(`${SEARCHABLE_DROPDOWN_TAG_NAME}[data-role='selector']`)
    ? dropdown
    : null;
}

export class FishyPresetQuickSwitch extends HTMLElementBase {
  constructor() {
    super();
    this.instanceId = nextQuickSwitchInstanceId;
    nextQuickSwitchInstanceId += 1;
    this._workspaceOverride = null;
    this._workspace = null;
    this._entriesOverride = null;
    this._contractResolverOverride = null;
    this._subscriptions = [];
    this._states = new Map();
    this._pending = new Set();
    this._errors = new Map();
    this._connected = false;
    this._renderKey = "";
    this._rowViews = new Map();
    this._list = null;
    this.heldUserMenus = new Map();
    this.lastPointerDown = null;
    this.handleSelectionChange = this.handleSelectionChange.bind(this);
    this.handleDropdownOpen = this.handleDropdownOpen.bind(this);
    this.handleDropdownClose = this.handleDropdownClose.bind(this);
    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
  }

  get workspace() {
    return this._workspaceOverride;
  }

  set workspace(value) {
    this._workspaceOverride = value ?? null;
    if (this._connected) {
      this.bindWorkspace();
    }
  }

  get entries() {
    return this._entriesOverride;
  }

  set entries(value) {
    this._entriesOverride = Array.isArray(value) ? value : null;
    if (this._connected) {
      this.bindWorkspace();
    }
  }

  get contractResolver() {
    return this._contractResolverOverride;
  }

  set contractResolver(value) {
    this._contractResolverOverride = typeof value === "function" ? value : null;
    if (this._connected) {
      this.render({ force: true });
    }
  }

  connectedCallback() {
    if (this._connected) {
      return;
    }
    this._connected = true;
    this.classList?.add?.("block", "w-full");
    this.addEventListener("change", this.handleSelectionChange);
    this.addEventListener(SEARCHABLE_DROPDOWN_OPEN_EVENT, this.handleDropdownOpen);
    this.addEventListener(SEARCHABLE_DROPDOWN_CLOSE_EVENT, this.handleDropdownClose);
    globalThis.document?.addEventListener?.("pointerdown", this.handleDocumentPointerDown, true);
    globalThis.window?.addEventListener?.(LANGUAGE_CHANGE_EVENT, this.handleLanguageChange);
    this.bindWorkspace();
  }

  disconnectedCallback() {
    if (!this._connected) {
      return;
    }
    this._connected = false;
    this.removeEventListener("change", this.handleSelectionChange);
    this.removeEventListener(SEARCHABLE_DROPDOWN_OPEN_EVENT, this.handleDropdownOpen);
    this.removeEventListener(SEARCHABLE_DROPDOWN_CLOSE_EVENT, this.handleDropdownClose);
    globalThis.document?.removeEventListener?.("pointerdown", this.handleDocumentPointerDown, true);
    globalThis.window?.removeEventListener?.(LANGUAGE_CHANGE_EVENT, this.handleLanguageChange);
    this.releaseSubscriptions();
    this.releaseAllUserMenus();
  }

  resolvedEntries() {
    return presetQuickSwitchEntries(this._entriesOverride ?? PRESET_COLLECTION_KEYS);
  }

  resolveContract(collectionKey) {
    const contract = (this._contractResolverOverride ?? presetCollectionContract)(collectionKey);
    return presetContractForWorkspace(contract, this._workspace, collectionKey);
  }

  resolveWorkspace() {
    if (this._workspaceOverride) {
      return this._workspaceOverride;
    }
    try {
      return browserPresetWorkspace();
    } catch (error) {
      for (const entry of this.resolvedEntries()) {
        this._errors.set(entry.collectionKey, error instanceof Error ? error.message : String(error));
      }
      return null;
    }
  }

  releaseSubscriptions() {
    for (const unsubscribe of this._subscriptions) {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      } else {
        unsubscribe?.unsubscribe?.();
      }
    }
    this._subscriptions = [];
  }

  bindWorkspace() {
    this.releaseSubscriptions();
    this._workspace = this.resolveWorkspace();
    for (const entry of this.resolvedEntries()) {
      const state = this._workspace
        ? readPresetWorkspaceState(this._workspace, entry.collectionKey)
        : { kind: "unavailable", collectionKey: entry.collectionKey };
      this._states.set(entry.collectionKey, state);
      if (typeof this._workspace?.subscribe === "function") {
        this._subscriptions.push(this._workspace.subscribe(entry.collectionKey, (nextState) => {
          if (!this._connected) {
            return;
          }
          this._states.set(entry.collectionKey, nextState ?? readPresetWorkspaceState(this._workspace, entry.collectionKey));
          this.render();
        }));
      }
    }
    this.render({ force: true });
    if (typeof this._workspace?.load === "function") {
      Promise.resolve(this._workspace.load()).then(() => {
        if (!this._connected) {
          return;
        }
        for (const entry of this.resolvedEntries()) {
          this._states.set(entry.collectionKey, readPresetWorkspaceState(this._workspace, entry.collectionKey));
        }
        this.render();
      }).catch((error) => {
        if (!this._connected) {
          return;
        }
        for (const entry of this.resolvedEntries()) {
          this._errors.set(entry.collectionKey, error instanceof Error ? error.message : String(error));
        }
        this.render({ force: true });
      });
    }
  }

  rows() {
    return buildPresetQuickSwitchRows(
      this._states,
      this.resolvedEntries(),
      translatePresetText,
      this.resolveContract.bind(this),
    );
  }

  updateRow(view, row, { refreshOptions = false } = {}) {
    const { dropdown, heading, status, body } = view;
    const trigger = dropdown.querySelector("[data-role='trigger']");
    const pending = this._pending.has(row.collectionKey);
    const optionsKey = JSON.stringify(row.options);
    const optionsChanged = refreshOptions || optionsKey !== view.optionsKey;
    const selectionChanged = dropdown.getAttribute("value") !== row.selectedOptionKey;
    if (optionsChanged) {
      dropdown.querySelector("[data-role='selected-content-catalog']").replaceWith(this.renderCatalog(row));
    }
    if (selectionChanged) {
      dropdown.setAttribute("value", row.selectedOptionKey);
    }
    if (dropdown.getAttribute("label") !== row.selectedLabel) {
      dropdown.setAttribute("label", row.selectedLabel);
    }
    const selectedContent = dropdown.querySelector("[data-role='selected-content']");
    if (selectedContent.textContent !== row.selectedLabel) {
      selectedContent.textContent = row.selectedLabel;
    }
    if (dropdown.getAttribute("placeholder") !== row.searchPlaceholder) {
      dropdown.setAttribute("placeholder", row.searchPlaceholder);
    }
    if (heading.textContent !== row.label) {
      heading.textContent = row.label;
    }
    const statusText = pending
      ? translatePresetText("presets.status.applying", "Applying…")
      : row.selectedStatus;
    if (status.textContent !== statusText) {
      status.textContent = statusText;
    }
    const statusClass = row.modified ? "badge badge-xs badge-warning badge-soft" : "badge badge-xs badge-ghost";
    if (status.className !== statusClass) {
      status.className = statusClass;
    }
    trigger.disabled = !row.ready || pending;
    if (pending !== view.pending) {
      trigger.lastElementChild.replaceWith(pending
        ? createElement("span", "loading loading-spinner loading-sm")
        : createIcon("caret-down", "size-4 shrink-0 opacity-60"));
    }
    const errorText = this._errors.get(row.collectionKey) || row.errorText;
    if (errorText) {
      if (!view.error) {
        view.error = createElement("p", "text-xs text-error");
        body.append(view.error);
      }
      if (view.error.textContent !== errorText) {
        view.error.textContent = errorText;
      }
    } else if (view.error) {
      view.error.remove();
      view.error = null;
    }
    if (!row.ready || pending) {
      dropdown.close?.();
    } else if ((optionsChanged || selectionChanged) && dropdown.isOpen?.()) {
      dropdown.refreshResults();
    }
    view.optionsKey = optionsKey;
    view.pending = pending;
  }

  render({ force = false, refreshOptions = false } = {}) {
    const rows = this.rows();
    const renderKey = `${presetQuickSwitchRenderKey(rows)}:${JSON.stringify([...this._pending])}:${JSON.stringify([...this._errors])}`;
    if (!force && renderKey === this._renderKey) {
      return;
    }
    this._renderKey = renderKey;
    if (!this._list) {
      this._list = createElement("div", "grid gap-2");
      this.replaceChildren(this._list);
    }
    const collectionKeys = new Set(rows.map((row) => row.collectionKey));
    for (const [collectionKey, view] of this._rowViews) {
      if (!collectionKeys.has(collectionKey)) {
        view.shell.remove();
        this._rowViews.delete(collectionKey);
      }
    }
    rows.forEach((row, index) => {
      let view = this._rowViews.get(row.collectionKey);
      if (!view) {
        const shell = this.renderRow(row);
        view = this._rowViews.get(row.collectionKey);
        this._list.insertBefore(shell, this._list.children[index] ?? null);
        // The dropdown hydrates its selected template after connection. Current's
        // dirty label is owned here, rather than by that reference template.
        queueMicrotask(() => {
          if (this._connected && this._rowViews.get(row.collectionKey) === view) {
            const currentRow = this.rows().find((candidate) => candidate.collectionKey === row.collectionKey);
            if (currentRow) this.updateRow(view, currentRow);
          }
        });
      } else {
        this.updateRow(view, row, { refreshOptions });
        if (this._list.children[index] !== view.shell) {
          this._list.insertBefore(view.shell, this._list.children[index] ?? null);
        }
      }
    });
  }

  renderRow(row) {
    const shell = createElement("section", "card card-border bg-base-100");
    shell.dataset.collectionKey = row.collectionKey;
    const body = createElement("div", "card-body gap-2 p-3");
    const header = createElement("div", "flex min-w-0 items-center justify-between gap-2");
    const heading = createElement("h3", "min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-base-content/60", row.label);
    header.append(heading);
    const status = createElement(
      "span",
      row.modified ? "badge badge-xs badge-warning badge-soft" : "badge badge-xs badge-ghost",
      this._pending.has(row.collectionKey)
        ? translatePresetText("presets.status.applying", "Applying…")
        : row.selectedStatus,
    );
    header.append(status);
    const dropdown = this.renderDropdown(row);
    body.append(header, dropdown);
    const errorText = this._errors.get(row.collectionKey) || row.errorText;
    const error = errorText ? createElement("p", "text-xs text-error", errorText) : null;
    if (error) body.append(error);
    shell.append(body);
    this._rowViews.set(row.collectionKey, {
      shell, body, heading, status, dropdown, error,
      optionsKey: JSON.stringify(row.options),
      pending: this._pending.has(row.collectionKey),
    });
    return shell;
  }

  renderDropdown(row) {
    const inputId = `fishy-preset-quick-switch-${this.instanceId}-${row.collectionKey}`;
    const panelId = `${inputId}-panel`;
    const searchId = `${inputId}-search`;
    const dropdown = createElement(SEARCHABLE_DROPDOWN_TAG_NAME, "relative block w-full");
    dropdown.dataset.role = "selector";
    dropdown.dataset.collectionKey = row.collectionKey;
    dropdown.setAttribute("label", row.selectedLabel);
    dropdown.setAttribute("value", row.selectedOptionKey);
    dropdown.setAttribute("placeholder", row.searchPlaceholder);
    dropdown.setAttribute("panel-mode", "detached");
    dropdown.setAttribute("panel-placement", "overlay-anchor");
    dropdown.setAttribute("panel-min-width", "panel");
    dropdown.setAttribute("panel-width", "22rem");

    const trigger = createElement("button", "btn btn-sm w-full min-w-0 justify-between");
    trigger.type = "button";
    trigger.dataset.role = "trigger";
    trigger.disabled = !row.ready || this._pending.has(row.collectionKey);
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-controls", panelId);
    const selectedContent = createElement("span", "min-w-0 truncate text-left", row.selectedLabel);
    selectedContent.dataset.role = "selected-content";
    trigger.append(selectedContent);
    if (this._pending.has(row.collectionKey)) {
      trigger.append(createElement("span", "loading loading-spinner loading-sm"));
    } else {
      trigger.append(createIcon("caret-down", "size-4 shrink-0 opacity-60"));
    }
    dropdown.append(trigger);

    const panel = createElement("div", "absolute left-0 top-0 z-50 w-full min-w-full max-w-full");
    panel.id = panelId;
    panel.dataset.role = "panel";
    panel.hidden = true;
    const panelShell = createElement("div", "grid min-w-full overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-lg");
    const searchLabel = createElement("label", "input input-sm m-2 w-auto");
    searchLabel.setAttribute("for", searchId);
    searchLabel.append(createIcon("search-field", "size-4 opacity-60"));
    const search = createElement("input", "grow");
    search.id = searchId;
    search.type = "search";
    search.autocomplete = "off";
    search.spellcheck = false;
    search.placeholder = row.searchPlaceholder;
    search.dataset.role = "search-input";
    searchLabel.append(search);
    const results = createElement("ul", "menu menu-sm max-h-64 w-full overflow-y-auto p-2");
    results.dataset.role = "results";
    results.tabIndex = -1;
    if (!row.options.length) {
      const li = createElement("li", "menu-disabled");
      li.append(createElement("span", "", row.emptyText));
      results.append(li);
    }
    panelShell.append(searchLabel, results);
    panel.append(panelShell);
    dropdown.append(panel);
    dropdown.append(this.renderCatalog(row));
    return dropdown;
  }

  renderCatalog(row) {
    const catalog = createElement("div");
    catalog.dataset.role = "selected-content-catalog";
    catalog.hidden = true;
    for (const option of row.options) {
      const selectedTemplate = document.createElement("template");
      selectedTemplate.dataset.role = "selected-content";
      selectedTemplate.dataset.value = option.key;
      selectedTemplate.dataset.label = option.label;
      selectedTemplate.dataset.searchText = option.searchText;
      selectedTemplate.content.append(createElement("span", "min-w-0 truncate", option.label));
      const optionTemplate = document.createElement("template");
      optionTemplate.dataset.role = "option-content";
      optionTemplate.dataset.value = option.key;
      const optionContent = createElement("span", "flex min-w-0 flex-1 items-center justify-between gap-2");
      optionContent.append(createElement("span", "min-w-0 truncate", option.label));
      optionContent.append(createElement(
        "span",
        "badge badge-xs badge-ghost",
        option.kind === "fixed"
          ? translatePresetText("presets.status.built_in", "Built in")
          : translatePresetText("common.status.saved", "Saved"),
      ));
      optionTemplate.content.append(optionContent);
      catalog.append(selectedTemplate, optionTemplate);
    }
    return catalog;
  }

  findOption(collectionKey, value) {
    return this.rows()
      .find((row) => row.collectionKey === collectionKey)
      ?.options.find((option) => option.key === value) ?? null;
  }

  async applyOptionValue(collectionKey, value) {
    const option = this.findOption(collectionKey, value);
    const state = this._states.get(collectionKey);
    if (!option || state?.kind !== "ready" || this._pending.has(collectionKey)) {
      return;
    }
    this._pending.add(collectionKey);
    this._errors.delete(collectionKey);
    this.render({ force: true });
    try {
      const result = await applyPresetQuickSwitchOption(this._workspace, option, state.revision);
      if (result?.kind === "ready") {
        this._states.set(collectionKey, result);
        toastHelper()?.success?.(
          translatePresetText("presets.toast.applied", "Using \“{$name}\”.", { name: option.label }),
        );
      } else {
        if (result?.kind === "conflict" && result.authoritative?.kind === "ready") {
          this._states.set(collectionKey, result.authoritative);
        }
        this._errors.set(collectionKey, presetWorkspaceStateMessage(result));
      }
    } catch (error) {
      this._errors.set(collectionKey, error instanceof Error ? error.message : String(error));
      toastHelper()?.error?.(this._errors.get(collectionKey));
    } finally {
      this._pending.delete(collectionKey);
      this.render({ force: true });
      const trigger = Array.from(this.querySelectorAll?.("[data-collection-key]") ?? [])
        .find((element) => element.dataset.collectionKey === collectionKey)
        ?.querySelector?.("[data-role='trigger']");
      focusWithoutScroll(trigger);
      this.releaseAllUserMenus();
    }
  }

  handleSelectionChange(event) {
    const dropdown = selectorFromEvent(event);
    if (!dropdown || !this.contains?.(dropdown)) {
      return;
    }
    void this.applyOptionValue(
      trimString(dropdown.dataset.collectionKey),
      trimString(dropdown.getAttribute("value")),
    );
  }

  handleDropdownOpen(event) {
    const dropdown = selectorFromEvent(event);
    if (dropdown && this.contains?.(dropdown)) {
      this.holdUserMenu(dropdown.closest?.(".dropdown"));
    }
  }

  handleDropdownClose(event) {
    const dropdown = selectorFromEvent(event);
    const menu = dropdown?.closest?.(".dropdown");
    if (menu && !this.recentPointerWasInsideMenu(menu)) {
      this.releaseUserMenu(menu);
    }
  }

  handleDocumentPointerDown(event) {
    const target = event?.target;
    if (!(target instanceof Node)) {
      return;
    }
    const insideMenus = new Set([...this.heldUserMenus.keys()].filter((menu) => menu.contains(target)));
    const insidePanel = target instanceof Element && Boolean(target.closest(`${TAG_NAME} [data-role='panel']`));
    this.lastPointerDown = { time: Date.now(), insideMenus };
    if (this.heldUserMenus.size && !insideMenus.size && !insidePanel) {
      this.releaseAllUserMenus();
    }
  }

  recentPointerWasInsideMenu(menu) {
    return Boolean(
      menu
      && this.lastPointerDown
      && Date.now() - this.lastPointerDown.time < 350
      && this.lastPointerDown.insideMenus.has(menu),
    );
  }

  holdUserMenu(menu) {
    if (!(menu instanceof HTMLElement)) {
      return;
    }
    const held = this.heldUserMenus.get(menu) ?? {
      count: 0,
      hadOpenClass: menu.classList.contains("dropdown-open"),
    };
    held.count += 1;
    menu.classList.add("dropdown-open");
    this.heldUserMenus.set(menu, held);
  }

  releaseUserMenu(menu) {
    const held = this.heldUserMenus.get(menu);
    if (!held) {
      return;
    }
    held.count -= 1;
    if (held.count > 0) {
      this.heldUserMenus.set(menu, held);
      return;
    }
    if (!held.hadOpenClass) {
      menu.classList.remove("dropdown-open");
    }
    this.heldUserMenus.delete(menu);
  }

  releaseAllUserMenus() {
    for (const [menu, held] of this.heldUserMenus) {
      if (!held.hadOpenClass) {
        menu.classList.remove("dropdown-open");
      }
    }
    this.heldUserMenus.clear();
  }

  handleLanguageChange() {
    this.render({ force: true, refreshOptions: true });
  }
}

export function registerPresetQuickSwitch(registry = globalThis.customElements) {
  if (globalThis.window?.customElements) {
    registerSearchableDropdown();
  }
  if (!registry || typeof registry.define !== "function" || registry.get?.(TAG_NAME)) {
    return;
  }
  registry.define(TAG_NAME, FishyPresetQuickSwitch);
}

registerPresetQuickSwitch();
